/**
 * Phase-7 HQ projection.
 *
 * This consumes only the session-owned Arena store. It never polls Portal,
 * opens a socket, or knows about application transport. RemoteStateController
 * remains the sole owner of bootstrap/cursor/recovery; this module is a pure
 * world projection of that already-authorized state.
 */
import * as pc from 'playcanvas'
import type {
  DaemonHealth,
  Interaction,
  NormalizedRun,
  ProjectedAgent,
  QueueEntry,
  TranscriptEvent,
} from '../../api/types'
import { useArenaStore } from '../../store'
import {
  CORE_BEAD_CAP,
  CORE_MAST_BOTTOM,
  CORE_MAST_TOP,
  CORE_X,
  CORE_Z,
  type HqCoreHandles,
} from './hqCore'
import { HQ_WATER_Y } from './hqFacadeLayout'
import type { HqFacadeHandles } from './hqFacade'
import type { HqProgramSummary } from './hqSummaryBoards'

export const HQ_PROJECTION_STALE_MS = 30_000
export const HQ_BOAT_LIFETIME_SECONDS = 12
/** Time for a completed-run bead to detach at the mast crown and clear the oculus. */
export const HQ_COMPLETION_BEAD_LIFETIME_SECONDS = 1.4
/** The roof oculus is at Y=18; completion beads remain visible above it before release. */
export const HQ_COMPLETION_BEAD_EXIT_Y = 22

export type ProjectedHealth = HqProgramSummary['health']

export interface HqProjectionTarget {
  health: ProjectedHealth
  color: readonly [number, number, number]
  pulseSpeed: number
  haloSpeed: number
  queueDepth: number
  activeRuns: number
  recentCompletions: number
  recentCompletionIds: readonly string[]
  summary: HqProgramSummary
}

export interface HqProjectionConsumers {
  projectHealth?(health: DaemonHealth | null): void
  projectRuns?(
    runs: NormalizedRun[],
    agents: ProjectedAgent[],
    interactionsByRun: Record<string, Interaction[]>,
    transcriptsByRun: Record<string, TranscriptEvent[]>,
  ): void
  projectAgentActivity?(
    interactionsByRun: Record<string, Interaction[]>,
    transcriptsByRun: Record<string, TranscriptEvent[]>,
  ): void
  projectProgramActivity?(activeRuns: number, recentCompletions: number): void
  projectProgramCooldown?(recentCompletionIds: readonly string[]): void
  projectProgramSummary?(summary: HqProgramSummary): void
}

const HEALTH_VIEW: Record<
  ProjectedHealth,
  { color: readonly [number, number, number]; pulseSpeed: number }
> = {
  healthy: { color: [0.21, 0.75, 0.48], pulseSpeed: 0.8 },
  degraded: { color: [0.88, 0.66, 0.23], pulseSpeed: 1.8 },
  down: { color: [0.84, 0.27, 0.27], pulseSpeed: 4.5 },
  unknown: { color: [0.18, 0.36, 0.54], pulseSpeed: 0.35 },
}

function time(value: string): number {
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY
}

function boundedCount(value: number, maximum = CORE_BEAD_CAP): number {
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(maximum, Math.floor(value)))
}

export function deriveHqProjection(
  health: DaemonHealth | null,
  runs: readonly NormalizedRun[],
  queue: readonly QueueEntry[],
  now = Date.now(),
): HqProjectionTarget {
  const checkedAt = health ? time(health.checkedAt) : Number.NEGATIVE_INFINITY
  const fresh =
    health !== null &&
    Number.isFinite(checkedAt) &&
    checkedAt <= now + 5_000 &&
    now - checkedAt <= HQ_PROJECTION_STALE_MS
  const status: ProjectedHealth = fresh ? health.status : 'unknown'
  const view = HEALTH_VIEW[status]
  const activeRuns = runs.filter((run) => run.status === 'running' && !run.stale).length
  const projectedQueueDepth = queue.filter((entry) => entry.status === 'pending').length
  const queueDepth = boundedCount(
    projectedQueueDepth || (fresh ? health.queueDepth : 0),
  )
  const recentCompletionIds = runs
    .filter((run) =>
      run.status === 'completed' &&
      now - time(run.updatedAt) >= 0 &&
      now - time(run.updatedAt) <= 60_000,
    )
    .sort((a, b) => time(b.updatedAt) - time(a.updatedAt) || a.id.localeCompare(b.id))
    .map((run) => run.id)
  const recentCompletions = recentCompletionIds.length
  const exactQueueDepth = projectedQueueDepth || (
    fresh && Number.isFinite(health.queueDepth)
      ? Math.max(0, Math.floor(health.queueDepth))
      : 0
  )
  return {
    health: status,
    color: view.color,
    pulseSpeed: view.pulseSpeed,
    haloSpeed: Math.min(4, 0.35 + activeRuns * 0.22),
    queueDepth,
    activeRuns: boundedCount(activeRuns),
    recentCompletions,
    recentCompletionIds,
    summary: {
      health: status,
      uptimeSeconds: fresh && Number.isFinite(health.uptime) && health.uptime >= 0
        ? Math.floor(health.uptime)
        : null,
      activeRuns,
      queueDepth: exactQueueDepth,
      totalRuns: runs.length,
      completedRuns: runs.filter((run) => run.status === 'completed').length,
      failedRuns: runs.filter((run) => run.status === 'failed').length,
      costSummary: fresh ? health.costSummary ?? null : null,
    },
  }
}

export function completedRunTransitions(
  previous: ReadonlyMap<string, string>,
  runs: readonly NormalizedRun[],
): string[] {
  return runs
    .filter((run) =>
      run.status === 'completed' &&
      previous.has(run.id) &&
      previous.get(run.id) !== 'completed',
    )
    .map((run) => run.id)
    .sort()
}

function hash(value: string): number {
  let result = 5381
  for (let index = 0; index < value.length; index++) {
    result = ((result << 5) + result + value.charCodeAt(index)) >>> 0
  }
  return result
}

function lerp(from: number, to: number, alpha: number): number {
  return from + (to - from) * alpha
}

interface ActiveBoat {
  entity: pc.Entity
  runId: string
  age: number
  z: number
}

interface ActiveCompletionBead {
  runId: string
  age: number
}

export class HqProjectionSystem {
  private target = deriveHqProjection(null, [], [])
  private color: [number, number, number] = [...this.target.color]
  private elapsed = 0
  private freshnessElapsed = 0
  private previousRunStatuses = new Map<string, string>()
  private generation = -1
  /** Session-scoped source; generation changes clear it before any consumer runs. */
  private sessionHealth: DaemonHealth | null = null
  private activeBoats: ActiveBoat[] = []
  private activeCompletionBeads: ActiveCompletionBead[] = []
  private unsubscribe?: () => void
  private readonly onUpdate = (dt: number): void => this.frame(dt)

  constructor(
    private readonly app: pc.Application,
    private readonly core: HqCoreHandles,
    private readonly facade: HqFacadeHandles,
    private readonly consumers: HqProjectionConsumers = {},
  ) {}

  init(): void {
    const initial = useArenaStore.getState()
    this.generation = initial.apiGeneration
    this.sessionHealth = initial.health
    this.previousRunStatuses = new Map(initial.runs.map((run) => [run.id, run.status]))
    this.sync(this.sessionHealth, initial.runs, initial.queue, false)
    this.consumers.projectHealth?.(this.projectableHealth(this.sessionHealth))
    this.consumers.projectRuns?.(
      initial.runs,
      initial.agents,
      initial.interactionsByRun,
      initial.transcriptsByRun,
    )
    this.projectPrograms()
    this.unsubscribe = useArenaStore.subscribe((state, previous) => {
      const generationChanged = state.apiGeneration !== this.generation
      if (generationChanged) {
        this.generation = state.apiGeneration
        this.sessionHealth = null
        this.clearBoats()
        this.clearCompletionBeads()
      } else if (state.health !== previous.health) {
        this.sessionHealth = state.health
      }
      const visualChanged =
        generationChanged ||
        state.health !== previous.health ||
        state.runs !== previous.runs ||
        state.queue !== previous.queue
      if (visualChanged) {
        this.sync(this.sessionHealth, state.runs, state.queue, !generationChanged)
        this.projectPrograms()
      }
      if (generationChanged || state.health !== previous.health) {
        this.consumers.projectHealth?.(this.projectableHealth(this.sessionHealth))
      }
      if (
        generationChanged ||
        state.runs !== previous.runs ||
        state.agents !== previous.agents
      ) {
        this.consumers.projectRuns?.(
          state.runs,
          state.agents,
          state.interactionsByRun,
          state.transcriptsByRun,
        )
      } else if (
        state.interactionsByRun !== previous.interactionsByRun ||
        state.transcriptsByRun !== previous.transcriptsByRun
      ) {
        this.consumers.projectAgentActivity?.(
          state.interactionsByRun,
          state.transcriptsByRun,
        )
      }
    })
    const events = this.app as pc.Application & {
      on?: (name: string, callback: (dt: number) => void) => void
    }
    events.on?.('update', this.onUpdate)
  }

  private projectableHealth(health: DaemonHealth | null): DaemonHealth | null {
    return this.target.health === 'unknown' ? null : health
  }

  private projectPrograms(): void {
    this.consumers.projectProgramActivity?.(
      this.target.activeRuns,
      this.target.recentCompletions,
    )
    this.consumers.projectProgramCooldown?.(this.target.recentCompletionIds)
    this.consumers.projectProgramSummary?.(this.target.summary)
  }

  private sync(
    health: DaemonHealth | null,
    runs: readonly NormalizedRun[],
    queue: readonly QueueEntry[],
    allowTransitions: boolean,
  ): void {
    if (allowTransitions) {
      for (const runId of completedRunTransitions(this.previousRunStatuses, runs)) {
        this.launchBoat(runId)
        this.launchCompletionBead(runId)
      }
    }
    this.previousRunStatuses = new Map(runs.map((run) => [run.id, run.status]))
    this.target = deriveHqProjection(health, runs, queue)
  }

  private launchBoat(runId: string): void {
    const occupied = new Set(this.activeBoats.map(({ entity }) => entity))
    const entity = this.facade.completionBoatPool.find((candidate) => !occupied.has(candidate))
    if (!entity) return
    const z = -55 + (hash(runId) % 110)
    entity.enabled = true
    entity.setLocalPosition(38, HQ_WATER_Y + 0.25, z)
    this.activeBoats.push({ entity, runId, age: 0, z })
  }

  private launchCompletionBead(runId: string): void {
    // Completion flights lease the same bounded pool as queue/active work.
    // A burst can never allocate or enable more than the Core's hard cap.
    if (this.activeCompletionBeads.length >= CORE_BEAD_CAP) return
    this.activeCompletionBeads.push({ runId, age: 0 })
  }

  frame(dt: number): void {
    const safeDt = Number.isFinite(dt) ? Math.max(0, Math.min(0.1, dt)) : 0
    this.elapsed += safeDt
    this.freshnessElapsed += safeDt
    // This is a local clock check, not a network poll. It lets an otherwise
    // quiet stream age to unknown instead of displaying old health forever.
    if (this.freshnessElapsed >= 1) {
      this.freshnessElapsed = 0
      const state = useArenaStore.getState()
      const previousHealth = this.target.health
      const previousCompletions = this.target.recentCompletions
      this.target = deriveHqProjection(this.sessionHealth, state.runs, state.queue)
      const healthChanged = this.target.health !== previousHealth
      if (healthChanged) {
        this.consumers.projectHealth?.(this.projectableHealth(this.sessionHealth))
      }
      if (
        healthChanged ||
        this.target.recentCompletions !== previousCompletions
      ) {
        this.projectPrograms()
      }
    }
    const alpha = 1 - Math.exp(-safeDt * 4)
    for (let index = 0; index < 3; index++) {
      this.color[index] = lerp(this.color[index], this.target.color[index], alpha)
    }
    const pulse =
      1 + Math.sin(this.elapsed * Math.PI * 2 * this.target.pulseSpeed) * 0.14
    const color = new pc.Color(...this.color)
    const dark = new pc.Color(
      this.color[0] * 0.18,
      this.color[1] * 0.18,
      this.color[2] * 0.18,
    )
    for (const material of [
      this.core.mastMaterial,
      this.core.orbMaterial,
      this.facade.healthSpandrelMaterial,
    ]) {
      material.diffuse = dark
      material.emissive = color
      material.emissiveIntensity = 1.5 * pulse
      material.update()
    }
    this.facade.skylineWindowMaterial.emissiveIntensity = Math.min(
      3,
      0.35 + this.target.activeRuns * 0.12 + this.target.queueDepth * 0.04,
    )
    this.facade.skylineWindowMaterial.update()
    this.core.setHaloSpeedMultiplier(this.target.haloSpeed)
    this.moveCompletionBeads(safeDt)
    this.positionBeads()
    this.moveBoats(safeDt)
  }

  private positionBeads(): void {
    const completionCount = this.activeCompletionBeads.length
    const queueCount = Math.min(
      this.target.queueDepth,
      CORE_BEAD_CAP - completionCount,
    )
    const activeCount = Math.min(
      this.target.activeRuns,
      CORE_BEAD_CAP - completionCount - queueCount,
    )
    const visible = completionCount + queueCount + activeCount
    for (let index = 0; index < this.core.beadPool.length; index++) {
      const bead = this.core.beadPool[index]
      bead.enabled = index < visible
      if (!bead.enabled) continue
      if (index < completionCount) {
        const flight = this.activeCompletionBeads[index]
        const progress = Math.min(
          1,
          flight.age / HQ_COMPLETION_BEAD_LIFETIME_SECONDS,
        )
        // Accelerate as the completion leaves the mast, then carry it visibly
        // above the Y=18 oculus. Tiny deterministic offsets separate bursts
        // while keeping every flight inside the 8 m x 7 m aperture.
        const eased = progress * progress
        const offset = ((hash(flight.runId) % 7) - 3) * 0.08
        bead.setLocalPosition(
          CORE_X + offset,
          lerp(CORE_MAST_TOP, HQ_COMPLETION_BEAD_EXIT_Y, eased),
          CORE_Z - offset * 0.6,
        )
      } else if (index < completionCount + queueCount) {
        const queueIndex = index - completionCount
        const angle =
          this.elapsed * 0.65 + (queueIndex / Math.max(1, queueCount)) * Math.PI * 2
        const radius = 2 + (queueIndex % 3) * 0.22
        bead.setLocalPosition(
          CORE_X + Math.cos(angle) * radius,
          1.45 + Math.sin(angle * 2) * 0.12,
          CORE_Z + Math.sin(angle) * radius,
        )
      } else {
        const activeIndex = index - completionCount - queueCount
        const progress =
          (this.elapsed * 0.22 + activeIndex / Math.max(1, activeCount)) % 1
        bead.setLocalPosition(
          CORE_X + ((activeIndex % 3) - 1) * 0.22,
          lerp(CORE_MAST_BOTTOM, CORE_MAST_TOP, progress),
          CORE_Z + ((activeIndex % 2) * 2 - 1) * 0.18,
        )
      }
    }
  }

  private moveCompletionBeads(dt: number): void {
    for (const flight of this.activeCompletionBeads) flight.age += dt
    this.activeCompletionBeads = this.activeCompletionBeads.filter(
      ({ age }) => age < HQ_COMPLETION_BEAD_LIFETIME_SECONDS,
    )
  }

  private moveBoats(dt: number): void {
    const remaining: ActiveBoat[] = []
    for (const boat of this.activeBoats) {
      boat.age += dt
      if (boat.age >= HQ_BOAT_LIFETIME_SECONDS) {
        boat.entity.enabled = false
        continue
      }
      boat.entity.setLocalPosition(
        38 + boat.age * 18,
        HQ_WATER_Y + 0.25,
        boat.z + Math.sin(boat.age * 0.6 + hash(boat.runId)) * 1.5,
      )
      remaining.push(boat)
    }
    this.activeBoats = remaining
  }

  private clearBoats(): void {
    for (const boat of this.activeBoats) boat.entity.enabled = false
    this.activeBoats = []
  }

  private clearCompletionBeads(): void {
    this.activeCompletionBeads = []
    for (const bead of this.core.beadPool) bead.enabled = false
  }

  destroy(): void {
    this.unsubscribe?.()
    const events = this.app as pc.Application & {
      off?: (name: string, callback: (dt: number) => void) => void
    }
    events.off?.('update', this.onUpdate)
    this.clearBoats()
    this.clearCompletionBeads()
    for (const bead of this.core.beadPool) bead.enabled = false
    this.core.setHaloSpeedMultiplier(1)
  }
}
